import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Share2, Check } from "lucide-react";

// Founder tiles now feature the brand's Power X mark (no personal initials).
// Each card gets a distinct treatment (background hue + arrow colour) so the
// spread feels like a photo book rather than four identical logos.
const FOUNDERS = [
  {
    key: "phil",
    name: "Phil-Kholynes Chidi",
    role: { en: "Founder & CEO", fr: "Fondateur et CEO", pt: "Fundador e CEO" },
    city: "Lagos, NG",
    since: "2024",
    quote: {
      en: "Sending money home should feel like sending a message. That's the whole product.",
      fr: "Envoyer de l'argent au pays devrait être aussi simple qu'envoyer un message. C'est tout le produit.",
      pt: "Enviar dinheiro para casa deve ser tão simples quanto mandar uma mensagem. É todo o produto.",
    },
    // Deep royal purple
    theme: {
      from: "#2D0D5E",
      via: "#4A1D96",
      to: "#7C3AED",
      arrows: "#E8FF00",
      dot: "#E8FF00",
      tile: "#4A1D96",
      chip: "text-lime",
      ring: "ring-lime/45",
    },
  },
  {
    key: "adaeze",
    name: "Adaeze O.",
    role: { en: "Head of Payments", fr: "Directrice des paiements", pt: "Diretora de pagamentos" },
    city: "Nairobi, KE",
    since: "2024",
    quote: {
      en: "Rails don't matter until they land the exact number, in minutes, every single time.",
      fr: "Les rails ne comptent que s'ils livrent le montant exact, en quelques minutes, à chaque fois.",
      pt: "Trilhos só importam quando entregam o número exato, em minutos, todas as vezes.",
    },
    // Deep forest / midnight
    theme: {
      from: "#041A10",
      via: "#0B3B2E",
      to: "#1E6E4A",
      arrows: "#E8FF00",
      dot: "#E8FF00",
      tile: "#0B3B2E",
      chip: "text-lime",
      ring: "ring-lime/55",
    },
  },
  {
    key: "kwame",
    name: "Kwame A.",
    role: { en: "Head of Product", fr: "Directeur produit", pt: "Diretor de produto" },
    city: "Accra, GH",
    since: "2024",
    quote: {
      en: "Every screen owes an answer. If it's not obvious in 90 seconds, we ship it again.",
      fr: "Chaque écran doit une réponse. Si ce n'est pas évident en 90 secondes, on le refait.",
      pt: "Toda tela deve uma resposta. Se não ficar óbvio em 90 segundos, refazemos.",
    },
    // Warm amber gradient
    theme: {
      from: "#3B2A0B",
      via: "#7A5A16",
      to: "#B48A1E",
      arrows: "#2D0D5E",
      dot: "#2D0D5E",
      tile: "#7A5A16",
      chip: "text-forest",
      ring: "ring-forest/25",
    },
  },
  {
    key: "marie",
    name: "Marie D.",
    role: { en: "Head of Trust & Compliance", fr: "Directrice conformité", pt: "Diretora de conformidade" },
    city: "Brussels, BE",
    since: "2024",
    quote: {
      en: "Compliance isn't paperwork. It's the promise that your money reaches, safely, every corridor.",
      fr: "La conformité, ce n'est pas de la paperasse. C'est la promesse que votre argent arrive en toute sécurité.",
      pt: "Conformidade não é papelada. É a promessa de que seu dinheiro chega, com segurança, em qualquer corredor.",
    },
    // Neon lime
    theme: {
      from: "#B8CC00",
      via: "#E8FF00",
      to: "#F4FF66",
      arrows: "#2D0D5E",
      dot: "#2D0D5E",
      tile: "#E8FF00",
      chip: "text-forest",
      ring: "ring-forest/30",
    },
  },
];

// Power X mark — reuses the header's arrow geometry but scales freely and
// accepts theme colours from its parent card.
function PowerXMark({ arrows, dot, size = 200, id }) {
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{
        filter: "drop-shadow(0 8px 30px rgba(0,0,0,0.25))",
      }}
    >
      <defs>
        <marker
          id={`px-ah-${id}`}
          viewBox="0 0 6 6"
          refX="1.2"
          refY="3"
          markerWidth="4"
          markerHeight="4"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L6,3 L0,6 L2,3 Z" fill={arrows} />
        </marker>
      </defs>
      <g stroke={arrows} strokeWidth="2.4" strokeLinecap="butt">
        <line x1="16" y1="16" x2="8.5" y2="8.5" markerEnd={`url(#px-ah-${id})`} />
        <line x1="16" y1="16" x2="23.5" y2="8.5" markerEnd={`url(#px-ah-${id})`} />
        <line x1="16" y1="16" x2="8.5" y2="23.5" markerEnd={`url(#px-ah-${id})`} />
        <line x1="16" y1="16" x2="23.5" y2="23.5" markerEnd={`url(#px-ah-${id})`} />
      </g>
      <circle cx="16" cy="16" r="1.9" fill={dot} />
    </svg>
  );
}

export default function FoundersPhotoBook() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const L = ["en", "fr", "pt"].includes(lang) ? lang : "en";

  return (
    <section
      className="py-24 md:py-32 bg-canvas"
      data-testid="founders-photobook"
    >
      <div className="container-x">
        <div className="max-w-2xl">
          <div className="label-eyebrow">{t("about.photobook.eyebrow")}</div>
          <h2 className="mt-4 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
            {t("about.photobook.title")}{" "}
            <span className="italic font-serif text-forest/80">
              {t("about.photobook.italic")}
            </span>
          </h2>
          <p className="mt-6 text-ink/70 leading-relaxed">
            {t("about.photobook.body")}
          </p>
        </div>

        {/* Editorial 2x2 spread */}
        <div className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10">
          {FOUNDERS.map((f, i) => (
            <FounderCard key={f.key} founder={f} lang={L} index={i} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FounderCard({ founder, lang, index, t }) {
  const isRotated = index % 2 === 1;
  const { theme } = founder;
  const [shared, setShared] = useState(false);
  const anchorId = `founder-${founder.key}`;

  const shareUrl = (() => {
    if (typeof window === "undefined") return "";
    const { origin, pathname } = window.location;
    const clean = pathname.replace(/\/$/, "");
    return `${origin}${clean}?founder=${founder.key}#${anchorId}`;
  })();

  const doShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      title: `${founder.name} · Instantxfer CashApp`,
      text: `${founder.name} — ${founder.role[lang]}. Meet the Founder Circle.`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(payload);
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    } catch (_) {
      // User cancelled the share sheet — no-op
    }
  };

  return (
    <motion.article
      id={anchorId}
      initial={{ opacity: 0, y: 40, rotate: isRotated ? 2 : -1.5 }}
      whileInView={{ opacity: 1, y: 0, rotate: isRotated ? 1 : -0.6 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.75,
        ease: [0.76, 0, 0.24, 1],
        delay: index * 0.08,
      }}
      whileHover={{ rotate: 0, y: -6 }}
      className="group relative scroll-mt-24"
      data-testid={`founder-card-${founder.key}`}
    >
      {/* Portrait tile */}
      <div
        className={`relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_40px_90px_-40px_rgba(4,26,16,0.55)] ring-1 ${theme.ring}`}
      >
        {/* Gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${theme.from} 0%, ${theme.via} 50%, ${theme.to} 100%)`,
          }}
        />
        {/* Grain */}
        <div
          className="absolute inset-0 opacity-[0.10] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence baseFrequency='0.9' /></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          }}
        />
        {/* Radial highlight */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.16), transparent 55%), radial-gradient(circle at 80% 90%, rgba(0,0,0,0.35), transparent 60%)",
          }}
        />

        {/* Power X mark — hero visual */}
        <div className="absolute inset-0 grid place-items-center">
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.15 + index * 0.08 }}
            className="group-hover:scale-[1.04] transition-transform duration-700"
          >
            <PowerXMark
              arrows={theme.arrows}
              dot={theme.dot}
              size={220}
              id={founder.key}
            />
          </motion.div>
        </div>

        {/* Instantxfer wordmark micro-lockup (top) */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-canvas/75 text-[10px] tracking-[0.28em] uppercase font-mono">
          <span>Portrait · No. {String(index + 1).padStart(2, "0")}</span>
          <span className={`${theme.chip} font-semibold`}>
            Instantxfer · CashApp
          </span>
        </div>

        {/* Caption band */}
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="font-display text-xl md:text-2xl text-canvas leading-tight">
                {founder.name}
              </div>
              <div className="mt-1 text-[11px] tracking-[0.2em] uppercase text-canvas/75">
                {founder.role[lang]} · {founder.city}
              </div>
            </div>
            <div className="shrink-0 text-right text-[10px] tracking-[0.22em] uppercase text-canvas/65 font-mono">
              <div>{t("about.photobook.captionSince")}</div>
              <div className={`${theme.chip} font-semibold mt-0.5`}>
                {founder.since}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote + share strip below the portrait */}
      <div className="mt-5 flex items-start gap-4">
        <blockquote className="flex-1 px-1 text-ink/75 text-sm md:text-base leading-relaxed font-serif italic">
          &ldquo;{founder.quote[lang]}&rdquo;
        </blockquote>
        <button
          type="button"
          onClick={doShare}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-forest/20 hover:border-forest/60 hover:bg-forest hover:text-canvas text-forest text-xs font-medium transition-colors"
          data-testid={`founder-share-${founder.key}`}
          aria-label={t("about.photobook.share")}
        >
          {shared ? (
            <>
              <Check className="w-3.5 h-3.5" strokeWidth={2.4} />
              {t("about.photobook.shareCopied")}
            </>
          ) : (
            <>
              <Share2 className="w-3.5 h-3.5" strokeWidth={2.4} />
              {t("about.photobook.share")}
            </>
          )}
        </button>
      </div>
    </motion.article>
  );
}
